/* eslint-disable import/no-cycle */
import * as React from 'react';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {observer} from 'mobx-react-lite';
import {Col, Row, Space} from 'antd';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Modal from '../../shared/modal/Modal';
import Button from '../../shared/buttons/Button';
import styles from '../InspectionTaskPendingWidget.module.scss';
import InspectionTaskExecutions from './InspectionTaskExecutions';
import DocumentsNavigator from '../../shared/dataDisplay/DocumentsNavigator';
import {useStore} from '../../../hooks/useStore';
import Form from '../../shared/form/Form';
import {getIcons} from '../getIcons';
import {taskManagement} from '../../../models/scope';
import {useModulePolicy} from '../../../hooks/useModulePolicy';
import hideInputStyles from './inspectionTaskExecutionModal.module.scss';
import GroupRemark from './GroupRemark';
import {EnDash} from '../../shared/unicodeWrapper/EnDash';
import {InspectionTaskPendingWidgetConfig} from '../InspectionTaskPendingWidgetConfig';
import InspectionTaskPendingWidget from '../InspectionTaskPendingWidget';

export const InspectionTaskContext = React.createContext(undefined);

// eslint-disable-next-line max-len
const InspectionTaskExecutionModal = ({
  visible,
  setVisible,
  pendingInspectionTask = null,
  inspectionTask = null,
  onCancel,
  onExecuted,
  openFirstDocument,
  ...props
}) => {
  const store = useStore();
  const {t} = useTranslation();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenDocument, setIsOpenDocument] = useState(false);
  const scopes = [taskManagement];
  const policy = useModulePolicy(store, scopes);
  const widgetConfig = new InspectionTaskPendingWidgetConfig(store, InspectionTaskPendingWidget.identifier);

  if (!visible) {
    return null;
  }

  let task = null;
  if (pendingInspectionTask) {
    task = pendingInspectionTask.inspectionTask;
  } else if (inspectionTask) {
    task = inspectionTask;
  } else {
    return null;
  }

  const hasDocuments = task?.documentIds?.length > 0;
  const tasks = store.inspectionTaskStore.getSameGroup(task, !!pendingInspectionTask);

  const onSubmit = async (values) => {
    setIsLoading(true);

    const groupRemark = form.getFieldValue('groupRemark');

    const toSave = [];
    Object.values(values.results).forEach((result) => {
      const {internal, ...data} = result;
      if (groupRemark) {
        if (data.remark) {
          data.remark = `${groupRemark} ${EnDash()} ${data.remark}`;
        } else {
          data.remark = groupRemark;
        }
      }
      toSave.push(data);
    });
    await store.inspectionTaskResultStore.create(toSave, {skipNotification: true});

    store.flashMessageStore.addFlashMessage({
      type: 'success', title: t('inspectionTask.resultSaved'),
    });

    setIsLoading(false);
    setVisible(false);
    if (onExecuted) {
      onExecuted();
    }
    form.resetFields();
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const modalTitle = (
    <Space>
      {pendingInspectionTask
      && getIcons(
        pendingInspectionTask,
        widgetConfig.getWidgetSetting('showInspectionTaskGroupIcon'),
        store.authStore.user?.roleIds,
        true
      ).map(({icon, style}) => (
        <FontAwesomeIcon key={icon.iconName} icon={icon} size="1x" style={style}/>
      ))}
      {task.inspectionTaskGroup?.name || task.name}
    </Space>
  );

  const modalFooter = (
    <Form
      name="rootForm"
      form={form}
      onFinish={onSubmit}
      initialValues={{results: {}, groupRemark: ''}}
    >
      <Space
        size={store.clientStore.isMobile ? 'small' : 'large'}
        direction={store.clientStore.isMobile ? 'vertical' : 'horizontal'}
      >
        <Form.Item
          className={hideInputStyles.messageOnly}
          style={store.clientStore.isMobile ? {display: 'block', width: '100%'} : {}}
          name="results"
          required
          rules={[{
            validator: (_, results) => {
              if (Object.keys(results).length === tasks.length) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t('inspectionTaskPendingModal.finishAllTasks')));
            },
          }]}
        >
          <></>
        </Form.Item>
        <div style={store.clientStore.isMobile ? {} : {padding: '10px 0'}}>
          <Button
            key={'cancel'}
            onClick={handleCancel}
          >
            {t('inspectionTaskPendingModal.cancel')}
          </Button>
        </div>
      </Space>
    </Form>
  );

  const openedDocument = openFirstDocument && hasDocuments ? task.documentsTree[0].document : undefined;

  return (
    <InspectionTaskContext.Provider value={{rootForm: form, tasks, isCreatingInspectionTaskResult: isLoading}}>
      <Form.Provider>
        <Modal
          className={styles.modalFullscreen}
          fullscreen
          title={modalTitle}
          footer={modalFooter}
          visible={visible}
          onCancel={onCancel}
          style={{margin: 0, minWidth: '100%'}}
          {...props}
        >
          <Row gutter={[16, {xs: 0, sm: 8, md: 16}]} style={{height: '100%'}}>
            <Col xs={24} lg={isOpenDocument ? 16 : 8}>
              {hasDocuments && (
                <DocumentsNavigator
                  allowDocumentCloseOnClick
                  documentTree={task.documentsTree}
                  openedDocument={openedDocument}
                  selectedKeys={openedDocument ? [task.documentsTree[0].key] : []}
                  onDocumentIdSelect={(id) => setIsOpenDocument(id)}
                  treeColProps={{xs: 24, lg: isOpenDocument ? 6 : 12}}
                  viewerColProps={{xs: 24, lg: isOpenDocument ? 18 : 24, style: {minHeight: '70vh', height: '100%'}}}
                  style={{height: '100%'}}
                />
              )}
            </Col>
            <Col xs={24} lg={8}>
              <Space direction={'vertical'} size={'middle'}>
                <GroupRemark
                  form={form}
                  showGroupRemark={widgetConfig.getWidgetSetting('showGroupRemark')}
                  maxLength={255}
                />
                <InspectionTaskExecutions
                  onSubmit={onSubmit}
                  inspectionTask={task}
                  disabled={!policy.canExecute()}
                  pendingOnly={!!pendingInspectionTask}
                  useUniformQualityIndicator={widgetConfig.getWidgetSetting('uniformQualityIndicator')}
                />
              </Space>
            </Col>
          </Row>
        </Modal>
      </Form.Provider>
    </InspectionTaskContext.Provider>
  );
};
export default observer(InspectionTaskExecutionModal);
